import { useState } from "react";
import { Alert, Button, DatePicker, Input, Rate, Select, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType, SortOrder } from "antd/es/table/interface";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import type { DishFeedback } from "@fitness-app/data-models/entities/DishFeedback";
import {
  FEEDBACK_PAGE_SIZE,
  useDishFeedbackList,
  type DishFeedbackFilters,
  type DishFeedbackListRow,
  type DishFeedbackSortKey,
} from "./useDishFeedbackList";

const SOURCE_LABELS = { DIET: "Z diety", PRIVATE: "Prywatna kopia", CUSTOM: "Własna potrawa" };
const sourceOptions = Object.entries(SOURCE_LABELS).map(([value, label]) => ({ value, label }));

const DEFAULT_SORT: Pick<DishFeedbackFilters, "sortBy" | "sortDir"> = { sortBy: "createdAt", sortDir: "desc" };

const SORT_KEYS: DishFeedbackSortKey[] = ["createdAt", "rating", "eventDate", "email"];
const isSortKey = (key: unknown): key is DishFeedbackSortKey => SORT_KEYS.includes(key as DishFeedbackSortKey);

// The table only reflects the sort: the query does the ordering (see useDishFeedbackList),
// so every sortable column is `sorter: true` with a controlled `sortOrder`.
const buildColumns = (
  sort: Pick<DishFeedbackFilters, "sortBy" | "sortDir">,
  withClient: boolean,
): ColumnsType<DishFeedbackListRow> => {
  const sortOrder = (key: DishFeedbackSortKey): SortOrder =>
    sort.sortBy === key ? (sort.sortDir === "asc" ? "ascend" : "descend") : null;

  const columns: ColumnsType<DishFeedbackListRow> = [
    {
      title: "Potrawa",
      dataIndex: "dishName",
      render: (name: string, row) => (
        <Space direction="vertical" size={4}>
          <Typography.Text strong>{name}</Typography.Text>
          <Tag>{SOURCE_LABELS[row.sourceKind]}</Tag>
          {!row.isCurrent || !row.isEaten ? (
            <Typography.Text type="secondary">
              {row.isCurrent ? "Cofnięto spożycie" : "Potrawa została zmieniona"}
            </Typography.Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Ocena",
      dataIndex: "rating",
      key: "rating",
      width: 160,
      sorter: true,
      sortOrder: sortOrder("rating"),
      render: (rating: number) => <Rate disabled value={rating} style={{ fontSize: 17 }} />,
    },
    {
      title: "Komentarz",
      dataIndex: "comment",
      render: (comment: string) => (
        <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxWidth: 480 }}>{comment || "—"}</div>
      ),
    },
    {
      title: "Dzień diety",
      dataIndex: "eventDate",
      key: "eventDate",
      width: 130,
      sorter: true,
      sortOrder: sortOrder("eventDate"),
      render: (date: string) => dayjs(date).format("DD.MM.YYYY"),
    },
    {
      title: "Data dodania",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 160,
      sorter: true,
      sortOrder: sortOrder("createdAt"),
      render: (date: string) => dayjs(date).format("DD.MM.YYYY HH:mm"),
    },
  ];

  if (!withClient) return columns;

  return [
    ...columns,
    {
      title: "Klientka",
      key: "email",
      width: 220,
      sorter: true,
      sortOrder: sortOrder("email"),
      render: (_: unknown, row) =>
        // No trainee row means the viewer may read the feedback but not the client (see useDishFeedbackList).
        row.trainee ? (
          <Space direction="vertical" size={0}>
            <Link to={`/trainee/details/${row.traineeId}`}>
              {[row.trainee.firstName, row.trainee.lastName].filter(Boolean).join(" ") || "Profil klientki"}
            </Link>
            <Typography.Text type="secondary" copyable={{ text: row.trainee.email }}>
              {row.trainee.email}
            </Typography.Text>
          </Space>
        ) : (
          <Link to={`/trainee/details/${row.traineeId}`}>Profil klientki</Link>
        ),
    },
  ];
};

export function DishFeedbackList({
  traineeId,
  nutritionDayId,
  feedbackId,
}: {
  traineeId?: string;
  nutritionDayId?: string;
  feedbackId?: string;
}) {
  const [filters, setFilters] = useState<DishFeedbackFilters>({ ...DEFAULT_SORT, page: 1 });
  const query = useDishFeedbackList({ ...filters, traineeId, nutritionDayId, feedbackId });
  const updateFilters = (values: Partial<DishFeedbackFilters>) =>
    setFilters((current) => ({ ...current, ...values, page: 1 }));
  const columns = buildColumns(filters, !traineeId);

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Typography.Title level={5} style={{ margin: 0 }}>
        Opinie o potrawach
      </Typography.Title>
      {feedbackId ? <Link to="/nutrition/feedback">Wszystkie opinie o potrawach</Link> : null}
      {!nutritionDayId && !feedbackId ? (
        <Space wrap>
          <Input.Search
            aria-label="Nazwa potrawy"
            placeholder="Szukaj potrawy"
            allowClear
            onSearch={(name) => updateFilters({ name })}
            style={{ width: 240 }}
          />
          {!traineeId ? (
            <Input.Search
              aria-label="Email klientki"
              placeholder="Szukaj po emailu"
              allowClear
              onSearch={(email) => updateFilters({ email })}
              style={{ width: 240 }}
            />
          ) : null}
          <Select
            aria-label="Ocena"
            placeholder="Wszystkie oceny"
            allowClear
            style={{ width: 170 }}
            options={[1, 2, 3, 4, 5].map((value) => ({ value, label: `${value}/5` }))}
            onChange={(rating) => updateFilters({ rating })}
          />
          <Select
            aria-label="Źródło potrawy"
            placeholder="Wszystkie źródła"
            allowClear
            style={{ width: 170 }}
            options={sourceOptions}
            onChange={(sourceKind: DishFeedback["sourceKind"] | undefined) => updateFilters({ sourceKind })}
          />
          <DatePicker.RangePicker
            onChange={(dates) =>
              updateFilters({ from: dates?.[0]?.format("YYYY-MM-DD"), to: dates?.[1]?.format("YYYY-MM-DD") })
            }
          />
        </Space>
      ) : null}
      {query.isError ? (
        <Alert
          type="error"
          showIcon
          message="Nie udało się pobrać opinii"
          action={<Button onClick={() => void query.refetch()}>Ponów</Button>}
        />
      ) : null}
      <Table
        rowKey="id"
        columns={columns}
        dataSource={query.data?.rows ?? []}
        loading={query.isLoading}
        scroll={{ x: 900 }}
        locale={{
          emptyText: feedbackId
            ? "Ta opinia została usunięta lub nie masz do niej dostępu."
            : "Brak opinii o potrawach",
        }}
        onChange={(_pagination, _tableFilters, sorter, extra) => {
          // Fires for page changes too, and `updateFilters` resets the page — so only react to the sort.
          if (extra.action !== "sort") return;
          const single = Array.isArray(sorter) ? sorter[0] : sorter;
          // antd's third click clears the sort; the list falls back to its default order then.
          if (!single?.order || !isSortKey(single.columnKey)) return updateFilters(DEFAULT_SORT);
          updateFilters({ sortBy: single.columnKey, sortDir: single.order === "ascend" ? "asc" : "desc" });
        }}
        pagination={{
          current: filters.page,
          pageSize: FEEDBACK_PAGE_SIZE,
          total: query.data?.count,
          showSizeChanger: false,
          onChange: (page) => setFilters((current) => ({ ...current, page })),
        }}
      />
      <Typography.Text type="secondary">
        Opinie dotyczą zjedzonej wersji potrawy. Prywatne kopie są oznaczone osobno.
      </Typography.Text>
    </Space>
  );
}
